import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetATSList } from "../../../api/ATSApi";
import { ATSItem } from "../../../api/ATS";
import { ENV } from "../../../GeneralConfig";
import ConnectIntegrationModal from "./ConnectIntegrationModal";
import IntegrationCard from "./IntegrationCard";
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

// Integration types with additional properties for UI
interface IntegrationType {
  id: string;
  name: string;
  logo: string;
  status: "active" | "coming_soon";
}

let integrationTypes: IntegrationType[] = [
  {
    id: "greenhouse",
    name: "Greenhouse",
    logo: "ats/greenhouse.svg", // Replace with actual logo path
    status: "active",
  },
  {
    id: "lever",
    name: "Lever",
    logo: "ats/lever.svg", // Replace with actual logo path
    status: "coming_soon",
  },
  {
    id: "workday",
    name: "Workday",
    logo: "ats/workday.svg", // Replace with actual logo path
    status: "active",
  },
  {
    id: "successfactors",
    name: "SAP SuccessFactors",
    logo: "ats/sap.svg", // Replace with actual logo path
    status: "active",
  },
  {
    id: "smartrecruiters",
    name: "Smart Recruiters",
    logo: "ats/smartrecruiters.svg", // Replace with actual logo path
    status: "coming_soon",
  },
  {
    id: "oraclerecruiting",
    name: "Oracle Recruiting Cloud",
    logo: "ats/oraclerecruiting.svg", // Replace with actual logo path
    status: "coming_soon",
  },
  {
    id: "icims",
    name: "iCIMS",
    logo: "ats/icims.svg", // Replace with actual logo path
    status: "active",
  },
  {
    id: "recruitee",
    name: "Recruitee",
    logo: "ats/recruitee.svg", // Replace with actual logo path
    status: "coming_soon",
  },
  {
    id: "teamtailor",
    name: "Team Tailor",
    logo: "ats/talor.svg", // Replace with actual logo path
    status: "coming_soon",
  },
  {
    id: "ashby",
    name: "Ashby",
    logo: "ats/ashby.svg", // Replace with actual logo path
    status: "coming_soon",
  },
  {
    id: "ukgpro",
    name: "UKG Pro",
    logo: "ats/ukpro.svg", // Replace with actual logo path
    status: "coming_soon",
  },
];
if (
  process.env.REACT_APP_ENVIRONMENT === "dev" ||
  process.env.REACT_APP_ENVIRONMENT === "qa"
) {
  integrationTypes.push({
    id: "sandbox",
    name: "Kombo Sandbox",
    logo: "ats/kombo.svg", // Replace with actual logo path
    status: "active",
  });
}

interface AlertConfig {
  message: string;
  type: "warning" | "success" | "error";
}

const Integrations: React.FC = () => {
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] =
    useState<IntegrationType | null>(null);
  const [activeIntegrations, setActiveIntegrations] = useState<ATSItem[]>([]);
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);

  // Get company ID from URL parameters
  const { companyId, subid } = useParams();
  // Use subid if available (for subcompanies), otherwise use companyId
  const currentCompanyId = subid || companyId;

  const {
    isLoading: loadingIntegrations,
    data: integrationsData,
    mutate: getATSList,
  } = useGetATSList();

  useEffect(() => {
    if (currentCompanyId) {
      getATSList({
        company: currentCompanyId,
      });
    }
  }, [getATSList, currentCompanyId]);

  useEffect(() => {
    if (integrationsData) {
      setActiveIntegrations(integrationsData);
    }
  }, [integrationsData]);

  const showAlert = (message: string, type: AlertConfig["type"]) => {
    setAlertConfig({ message, type });
  };

  const dismissAlert = () => {
    setAlertConfig(null);
  };

  const handleOpenConnectModal = (integration: IntegrationType) => {
    // Check if we're in production and should show the sandbox warning
    if (ENV === "production" && !integration.id.includes("sandbox")) {
      if (
        !window.confirm(
          "It's recommended to first test integrations in the sandbox environment. Do you want to continue with production setup?"
        )
      ) {
        return;
      }
    }

    setSelectedIntegration(integration);
    setIsConnectModalOpen(true);
  };

  const handleCloseConnectModal = () => {
    setIsConnectModalOpen(false);
    setSelectedIntegration(null);
  };

  const handleIntegrationSuccess = () => {
    if (currentCompanyId) {
      getATSList({
        company: currentCompanyId,
      });
    }
    handleCloseConnectModal();
    // Alert will be shown by onConnectionSetup callback now
  };

  // Check if an integration is already connected
  const isIntegrationConnected = (integrationId: string) => {
    return activeIntegrations.some((item) => item.tool === integrationId);
  };

  // Get connection details for an integration
  const getConnectionDetails = (integrationId: string) => {
    return activeIntegrations.find((item) => item.tool === integrationId);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Alert Bar */}
      {alertConfig && (
        <div
          className={`p-4 rounded-md flex items-center justify-between ${
            alertConfig.type === "warning"
              ? "bg-yellow-100 text-yellow-700"
              : alertConfig.type === "success"
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          <div className="flex items-center">
            {alertConfig.type === "warning" && (
              <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            )}
            {/* Add other icons for success/error if needed */}
            <span>{alertConfig.message}</span>
          </div>
          <button onClick={dismissAlert} className="ml-4">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      <h2 className="text-xl font-medium mb-4">Integrations</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {integrationTypes
          .sort((a, b) => {
            // First priority: connected integrations
            const aConnected = isIntegrationConnected(a.id);
            const bConnected = isIntegrationConnected(b.id);

            if (aConnected && !bConnected) return -1;
            if (!aConnected && bConnected) return 1;

            // Second priority: active status vs coming soon
            if (a.status === "active" && b.status === "coming_soon") return -1;
            if (a.status === "coming_soon" && b.status === "active") return 1;

            // Third priority: alphabetical by name
            return a.name.localeCompare(b.name);
          })
          .map((integration) => (
            <IntegrationCard
              key={integration.id}
              integration={integration}
              isConnected={isIntegrationConnected(integration.id)}
              connectionDetails={getConnectionDetails(integration.id)}
              onConnect={() => handleOpenConnectModal(integration)}
              isLoading={loadingIntegrations}
              onShowAlert={showAlert}
            />
          ))}
      </div>

      {selectedIntegration && (
        <ConnectIntegrationModal
          open={isConnectModalOpen}
          onClose={handleCloseConnectModal}
          integration={selectedIntegration}
          onSuccess={handleIntegrationSuccess}
          companyId={currentCompanyId}
          onConnectionSetup={showAlert}
        />
      )}
    </div>
  );
};

export default Integrations;
